import { useId, useRef, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FeldHilfe } from '@/components/FeldHilfe'
import { useBildEntfernen, useBildVerwendung, useProduktbilder, type Produktbild } from '@/api/produktbilder'
import { BildDialog, BildVorschau, ROLLEN } from './BildDialog'
import { LocalProductPhoto, PrimaryImageButton } from '@/components/ProductPhoto'
import { useResolvedImages, type ResolvedPhoto } from '@/api/produktbilder'

export function BilderBlock({ sku, artikelname }: { sku: string; artikelname: string }) {
  const bilder = useProduktbilder(sku)
  const resolved = useResolvedImages([sku])
  const photoFor = (bild: Produktbild): ResolvedPhoto | null => bild.file_hash ? {
    fileHash: bild.file_hash, publicUrl: resolved.data?.results[0]?.gallery.find(p => p.fileHash === bild.file_hash)?.publicUrl ?? '',
    sourceSku: sku, match: bild.is_primary ? 'manual' : 'exact', variantSku: bild.variant_sku,
    usageId: bild.id, seoName: bild.seo_name, altText: bild.alt_text, sourcePortals: bild.portals ?? [], origin: 'own',
  } : null
  const verwendung = useBildVerwendung(sku)
  const entfernen = useBildEntfernen(sku)
  const [target, setTarget] = useState<{ file: File } | { bild: Produktbild } | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Produktbild | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const actionLock = useRef(false)
  const input = useRef<HTMLInputElement>(null)
  const uploadId = useId()
  const data = [...(bilder.data ?? [])].sort((a, b) => a.position - b.position || a.id - b.id)
  const full = data.length >= 12

  function choose(files: FileList | File[]) {
    setError('')
    if (full) { setError('Maximal 12 Bilder je Artikel. Zuerst eine Verwendung entfernen.'); return }
    if (files.length !== 1) { setError('Bitte genau eine Bilddatei auswählen.'); return }
    const file = files[0]
    if (file.size > 15 * 1024 * 1024) { setError('Die Datei ist zu groß. Erlaubt sind höchstens 15 MB.'); return }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) { setError('Nur JPEG, PNG oder WebP sind erlaubt.'); return }
    setTarget({ file })
  }
  async function action(fn: () => Promise<unknown>) {
    if (actionLock.current) return
    actionLock.current = true
    setBusy(true)
    setError('')
    try { await fn() }
    catch (err) { setError(err instanceof Error ? err.message : 'Bildverwendung konnte nicht gespeichert werden') }
    finally { actionLock.current = false; setBusy(false) }
  }
  async function move(index: number, direction: -1 | 1) {
    const current = data[index]
    const other = data[index + direction]
    if (!other) return
    await action(async () => {
      if (current.position === other.position || current.position > 11 || other.position > 11) {
        // Alte URL-Positionen und max+1 aus dem Archiv auf den Update-Bereich 0–11 abbilden.
        const ordered = [...data]
        ;[ordered[index], ordered[index + direction]] = [other, current]
        for (const [position, bild] of ordered.entries()) {
          if (bild.position !== position) await verwendung.mutateAsync({ id: bild.id, payload: { position } })
        }
      } else {
        await verwendung.mutateAsync({ id: current.id, payload: { position: other.position } })
        await verwendung.mutateAsync({ id: other.id, payload: { position: current.position } })
      }
    })
  }
  return (
    <section id="inhalt-bilder" className="min-w-0 rounded-md border border-line-2 bg-panel">
      <header className="flex items-center justify-between gap-3 border-b border-line-2 px-4 py-3 sm:px-5">
        <div className="flex items-center gap-1"><h3 className="font-display text-base font-semibold text-ink">Bilder</h3><FeldHilfe feld="bilder" /></div>
        <span className="font-mono text-xs text-ink-2">{data.length}/12</span>
      </header>
      <div className="min-w-0 space-y-4 p-4 sm:p-5">
        {bilder.error && <p role="alert" className="break-words text-sm text-coral">{bilder.error.message}</p>}
        {error && <p role="alert" className="break-words text-sm text-coral">{error}</p>}
        <div data-testid="bild-ablagezone" className="rounded-md border border-dashed border-line-2 bg-panel-2 p-4 text-center"
          onDragOver={event => event.preventDefault()}
          onDrop={event => { event.preventDefault(); if (!busy && !bilder.isPending && !bilder.error) choose(event.dataTransfer.files) }}>
          <Upload className="mx-auto mb-2 size-5 text-primary" aria-hidden />
          <p className="text-sm text-ink">{data.length === 0 ? 'Produktbilder hier ablegen oder ein erstes Bild hochladen.' : 'Weiteres Produktbild hier ablegen.'}</p>
          <p className="mt-1 text-xs text-ink-2">JPEG, PNG oder WebP · höchstens 15 MB · maximal 12 Bilder</p>
          <input ref={input} id={uploadId} type="file" aria-label="Bild hochladen" accept="image/jpeg,image/png,image/webp"
            className="sr-only" disabled={full || busy || bilder.isPending || !!bilder.error} tabIndex={-1}
            onChange={event => { if (event.target.files?.length) choose(event.target.files); event.target.value = '' }} />
          <Button type="button" className="mt-3" disabled={full || busy || bilder.isPending || !!bilder.error} onClick={() => input.current?.click()}>Bild hochladen</Button>
        </div>
        {bilder.isLoading && <p className="text-sm text-ink-2">Bilder werden geladen…</p>}
        <div className="grid min-w-0 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {data.slice(0, 12).map((bild, index) => <article key={bild.id}
            className={`min-w-0 rounded-md border p-3 ${bild.is_primary ? 'border-primary' : 'border-line-2'}`}>
            {resolved.data?.enabled === false ? <BildVorschau src={bild.public_url} alt={bild.beschreibung ?? bild.alt_text ?? 'Produktbild'} className="h-36 w-full rounded bg-panel-2" />
              : photoFor(bild) ? <LocalProductPhoto photo={photoFor(bild)!} alt={bild.beschreibung ?? bild.alt_text ?? 'Produktbild'} className="h-36 w-full rounded bg-panel-2" />
              : <p className="h-36 text-xs text-ink-2">Archivierung ausstehend</p>}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className={`rounded px-2 py-0.5 text-xs font-semibold ${bild.is_primary ? 'bg-primary text-primary-foreground' : 'bg-panel-2 text-ink'}`}>
                {bild.is_primary ? 'Hauptbild' : bild.role ? ROLLEN[bild.role] : 'Ohne Rolle'}
              </span>
              <span className="text-xs text-ink-2">{bild.portals?.length ? bild.portals.join(', ') : bild.portal ?? 'Alle Portale'}</span>
            </div>
            <p className="mt-2 break-words text-sm text-ink">{bild.beschreibung ?? bild.alt_text ?? 'Noch keine Beschreibung'}</p>
            {bild.variant_sku && <p className="mt-1 break-all font-mono text-xs text-ink-2">{bild.variant_sku}</p>}
            <div className="mt-3 flex flex-wrap gap-2">
              <Button type="button" size="sm" variant="outline" aria-label="Nach oben" disabled={busy || index === 0} onClick={() => void move(index, -1)}>↑</Button>
              <Button type="button" size="sm" variant="outline" aria-label="Nach unten" disabled={busy || index === data.length - 1} onClick={() => void move(index, 1)}>↓</Button>
              <PrimaryImageButton sku={sku} photo={photoFor(bild)} usageId={bild.id} isManualPrimary={bild.is_primary} />
              <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => setTarget({ bild })}>Bearbeiten</Button>
              <Button type="button" size="sm" variant="ghost" className="text-coral" disabled={busy} onClick={() => setDeleteTarget(bild)}>Entfernen</Button>
            </div>
          </article>)}
        </div>
      </div>
      {target && <BildDialog sku={sku} artikelname={artikelname} {...target} onClose={() => setTarget(null)} />}
      <Dialog.Root open={!!deleteTarget} onOpenChange={open => { if (!open && !busy) setDeleteTarget(null) }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line-2 bg-panel p-5 text-ink shadow-lg">
            <Dialog.Title className="font-display font-semibold">Bildverwendung entfernen?</Dialog.Title>
            <Dialog.Description className="mt-2 text-sm text-ink-2">Entfernt nur die Verwendung am Artikel, das Bild bleibt im Archiv.</Dialog.Description>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <Button type="button" variant="outline" disabled={busy} onClick={() => setDeleteTarget(null)}>Abbrechen</Button>
              <Button type="button" disabled={busy} onClick={() => void action(async () => { await entfernen.mutateAsync(deleteTarget!.id); setDeleteTarget(null) })}>Verwendung entfernen</Button>
            </div>
            {error && <p role="alert" className="mt-3 break-words text-sm text-coral">{error}</p>}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </section>
  )
}
